import { Search, Truck } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { StatusBadge } from "@/components/status-badge"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { deliveryTraceabilityTone } from "@/features/cooperatives/lib/cooperative-metrics"
import { formatNumber } from "@/lib/format"
import type { CooperativeDeliveryEmbed } from "@/types/domain"

interface CooperativeDeliveriesTableProps {
  deliveries: CooperativeDeliveryEmbed[] | null | undefined
}

const headCellClass =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const bodyCellClass = "px-4 py-4"

export function CooperativeDeliveriesTable({
  deliveries,
}: CooperativeDeliveriesTableProps) {
  const { t } = useTranslation(["cooperatives", "common"])
  const [search, setSearch] = useState("")
  const mt = t("units.mt", { ns: "common" })

  const sorted = useMemo(() => {
    const list = (deliveries ?? []).slice()
    list.sort((a, b) => {
      const aT = a.date ? new Date(a.date).getTime() : 0
      const bT = b.date ? new Date(b.date).getTime() : 0
      return bT - aT
    })
    return list
  }, [deliveries])

  const filtered = useMemo(() => {
    const lower = search.toLowerCase().trim()
    if (!lower) return sorted
    return sorted.filter(
      (d) =>
        d.deliveryNumber?.toLowerCase().includes(lower) ||
        d.orderNumber?.toLowerCase().includes(lower) ||
        d.supplier?.toLowerCase().includes(lower) ||
        d.customer?.toLowerCase().includes(lower)
    )
  }, [sorted, search])

  const total = sorted.length

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Truck className="size-5 text-muted-foreground" />
          <h2 className="text-base font-semibold">
            {t("deliveries.title")} ({total})
          </h2>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("deliveries.search")}
            className="h-10 pl-9"
          />
        </div>
      </div>
      <Separator />

      {filtered.length === 0 ? (
        <CardContent className="p-0">
          <EmptyState
            title={t("deliveries.empty")}
            className="border-0 py-12"
          />
        </CardContent>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className={headCellClass}>
                  {t("deliveries.table.cccNumber")}
                </TableHead>
                <TableHead className={headCellClass}>
                  {t("deliveries.table.supplier")}
                </TableHead>
                <TableHead className={`${headCellClass} text-right`}>
                  {t("deliveries.table.quantity")}
                </TableHead>
                <TableHead className={headCellClass}>
                  {t("deliveries.table.orderNumber")}
                </TableHead>
                <TableHead className={headCellClass}>
                  {t("deliveries.table.traceability")}
                </TableHead>
                <TableHead className={headCellClass}>
                  {t("deliveries.table.eudr")}
                </TableHead>
                <TableHead className={`${headCellClass} text-right`} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((delivery) => {
                const trace = deliveryTraceabilityTone(
                  delivery.traceabilityStatus
                )
                return (
                  <TableRow
                    key={String(delivery.id)}
                    className="hover:bg-muted/40"
                  >
                    <TableCell className={`${bodyCellClass} font-medium`}>
                      {delivery.deliveryNumber || "—"}
                    </TableCell>
                    <TableCell
                      className={`${bodyCellClass} max-w-[200px] truncate`}
                    >
                      {delivery.supplier ?? "—"}
                    </TableCell>
                    <TableCell
                      className={`${bodyCellClass} text-right tabular-nums`}
                    >
                      {formatNumber(delivery.quantity ?? 0)} {mt}
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      <div>{delivery.orderNumber ?? "—"}</div>
                      {delivery.customer ? (
                        <div className="text-xs text-muted-foreground">
                          {delivery.customer}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      <StatusBadge tone={trace.tone}>
                        {t(`deliveries.traceability.${trace.bucket}`)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className={bodyCellClass}>
                      <StatusBadge
                        tone={delivery.eudrCompliant ? "success" : "danger"}
                      >
                        {delivery.eudrCompliant
                          ? t("deliveries.eudr.compliant")
                          : t("deliveries.eudr.nonCompliant")}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className={`${bodyCellClass} text-right`}>
                      {delivery.isLocked ? null : (
                        <StatusBadge tone="warning">
                          {t("deliveries.actions.inProgress")}
                        </StatusBadge>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  )
}
